import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { GangSearchUser } from '../../hooks/useGangMemberSelection';
import { ReactNode } from 'react';

interface GangMemberSearchResultsProps {
  searchQuery: string;
  users: GangSearchUser[];
  isLoading: boolean;
  canSelectMore: boolean;
  onSelectUser: (user: GangSearchUser) => void;
  onLoadMore: () => void;
  canLoadMore: boolean;
}

export const GangMemberSearchResults = ({
  searchQuery,
  users,
  isLoading,
  canSelectMore,
  onSelectUser,
  onLoadMore,
  canLoadMore,
}: GangMemberSearchResultsProps) => {
  const showSearchPrompt = searchQuery.length === 0;
  const needsMoreCharacters = searchQuery.length > 0 && searchQuery.length < 3;
  const noResults = searchQuery.length >= 3 && users.length === 0 && !isLoading;
  const showInitialLoading =
    isLoading && searchQuery.length >= 3 && users.length === 0;

  return (
    <Box
      sx={{
        maxHeight: '400px',
        overflowY: 'auto',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1,
        backgroundColor: 'background.default',
      }}
    >
      {showSearchPrompt && (
        <EmptyState
          icon={<SearchIcon sx={{ fontSize: 48, opacity: 0.5 }} />}
          message="Please search for users above to see available options."
        />
      )}

      {needsMoreCharacters && (
        <EmptyState
          icon={<SearchIcon sx={{ fontSize: 48, opacity: 0.5 }} />}
          message="Please enter at least 3 characters to search for users."
          messageColor="info.main"
        />
      )}

      {noResults && (
        <EmptyState
          icon={<SearchIcon sx={{ fontSize: 48, opacity: 0.5 }} />}
          message="No users found matching your search. Try a different term."
          messageColor="warning.main"
        />
      )}

      {showInitialLoading && (
        <EmptyState
          icon={
            <CircularProgress
              data-testid="search-loading-indicator"
              size={48}
            />
          }
          message="Searching for users..."
        />
      )}

      {users.length > 0 &&
        users.map(user => (
          <Paper
            key={user.id}
            sx={{
              p: 2,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              opacity: canSelectMore ? 1 : 0.5,
              cursor: canSelectMore ? 'pointer' : 'not-allowed',
              '&:hover': {
                backgroundColor: canSelectMore ? 'action.hover' : 'inherit',
              },
            }}
            data-testid={`available-${user.id}`}
            onClick={() => canSelectMore && onSelectUser(user)}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user.fullName.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {user.fullName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              size="small"
              color="primary"
              disabled={!canSelectMore}
              onClick={event => {
                event.stopPropagation();
                if (canSelectMore) {
                  onSelectUser(user);
                }
              }}
              data-testid={`select-button-${user.id}`}
            >
              Select
            </Button>
          </Paper>
        ))}

      {users.length > 0 && canLoadMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={isLoading}
            fullWidth
            sx={{ maxWidth: '200px' }}
            data-testid="load-more-button"
          >
            Load More
          </Button>
        </Box>
      )}
    </Box>
  );
};

interface EmptyStateProps {
  icon: ReactNode;
  message: string;
  messageColor?: string;
}

const EmptyState = ({ icon, message, messageColor }: EmptyStateProps) => (
  <Box
    sx={{
      py: 4,
      textAlign: 'center',
      color: messageColor ?? 'text.secondary',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>{icon}</Box>
    <Typography variant="body2">{message}</Typography>
  </Box>
);
